import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';

// ── Component ────────────────────────────────────────────────────────

export function CompanySettingsScreen() {
  const { theme } = useTheme();

  const [officerLabel, setOfficerLabel] = useState<string | null>(null);
  const [officerPluralLabel, setOfficerPluralLabel] = useState<string | null>(null);
  const [isMdtEntryPromptEnabled, setIsMdtEntryPromptEnabled] = useState(false);

  // Load settings on mount
  useEffect(() => {
    api.getCompanySettings(
      (successResult: any) => {
        setOfficerLabel(successResult.companySettings?.officerLabel ?? null);
        setOfficerPluralLabel(successResult.companySettings?.officerPluralLabel ?? null);
        setIsMdtEntryPromptEnabled(successResult.companySettings?.isMdtEntryPromptEnabled ?? false);
      },
      () => {
        App.showPopupNotification('Error getting settings.', 'error');
      }
    );
  }, []);

  const settingsLoaded = useMemo(
    () => officerLabel !== null && officerPluralLabel !== null,
    [officerLabel, officerPluralLabel]
  );

  const handleUpdate = useCallback(() => {
    const data = {
      officerLabel,
      officerPluralLabel,
      isMdtEntryPromptEnabled,
    };
    api.updateCompanySettings(
      data,
      () => {
        App.showPopupNotification('Company settings saved.', 'success');
      },
      () => {
        App.showPopupNotification('Error updating settings.', 'error');
      }
    );
  }, [officerLabel, officerPluralLabel, isMdtEntryPromptEnabled]);

  return (
    <div className={theme}>
      <div className="kst-page-container">
        <div className="kst-layout kst-layout--no-sidebar">
          <div className="kst-main-content">
            <div className="kst-page">
              <div className="kst-page-header">
                <h3 className="kst-page-title">Company Settings</h3>
                <p className="kst-page-description">
                  Configure general settings for your company.
                </p>
              </div>

              {settingsLoaded ? (
                <div className="kst-settings-form">
                  {/* Officer Label Settings */}
                  <div className="kst-settings-section">
                    <h4 className="kst-settings-section-title">Officer Label Settings</h4>
                    <p className="kst-settings-section-description">
                      Officer Label and Officers (plural) Label give you the flexibility to change the word "Officer" and the plural "Officers" in Orna to a different word.
                      Use the capitalized form of the word you wish to use to refer to officers. Orna will not automatically capitalize it.
                      After saving, users must log out and in again before seeing the new word.
                    </p>
                    <div className="kst-form-group">
                      <label className="kstc-label" htmlFor="inpOfficerLabel">Officer Label</label>
                      <input type="text" id="inpOfficerLabel" className="kstc-input" value={officerLabel || ''} onChange={(e) => setOfficerLabel(e.target.value)} />
                    </div>
                    <div className="kst-form-group">
                      <label className="kstc-label" htmlFor="inpOfficerPluralLabel">Officers (plural) Label</label>
                      <input type="text" id="inpOfficerPluralLabel" className="kstc-input" value={officerPluralLabel || ''} onChange={(e) => setOfficerPluralLabel(e.target.value)} />
                    </div>
                  </div>

                  {/* Mobile Module Settings */}
                  <div className="kst-settings-section">
                    <h4 className="kst-settings-section-title">Mobile Module Settings</h4>
                    <div className="kst-checkbox-group">
                      <input type="checkbox" id="chkMdtEntryPromptEnabled" className="kstc-checkbox" checked={isMdtEntryPromptEnabled} onChange={(e) => setIsMdtEntryPromptEnabled(e.target.checked)} />
                      <label className="kstc-checkbox-label" htmlFor="chkMdtEntryPromptEnabled">Enable Mobile Module Entry Prompt</label>
                    </div>
                    <p className="kst-checkbox-description">
                      When enabled, users will be prompted to confirm or update their call sign and assigned vehicle the first time they enter the Mobile module after logging in.
                    </p>
                  </div>

                  <div className="kst-settings-actions">
                    <button type="button" className="kstc-btn kstc-btn--primary" onClick={handleUpdate}>Save</button>
                  </div>
                </div>
              ) : (
                <div className="kst-loading"></div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanySettingsScreen;

